import React from 'react';

import {
  RegressionInterpolationInputStepDataName,
  RegressionStepDataName,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {getLegacyNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {DraftStepInputParents} from 'pipelines/network/PipelineDAO';

import {StepInputParentsConfigurationProps} from '../generalized-step-config-editor/StepInputParentsConfiguration';
import PartitionValueSelector from './PartitionValueSelector';
import RegressionModelSelector from './RegressionModelSelector';

/**
 * If defined, get the regression step's ID from an interpolation step's draft step input parents.
 */
function getRegressionStepId(stepInputParents: DraftStepInputParents): string | null {
  return stepInputParents.hasOwnProperty(RegressionInterpolationInputStepDataName.MODEL_VARIABLES)
    ? stepInputParents[RegressionInterpolationInputStepDataName.MODEL_VARIABLES][0].stepId
    : null;
}

/**
 * Update the draft stepInputParents with a new regression step's ID without changing any other
 * defined input parents.
 */
function getUpdatedStepInputParentInformation(
  stepInputParents: DraftStepInputParents,
  newRegressionStep: GetAnalysisPipelineStep
): DraftStepInputParents {
  return {
    ...stepInputParents,
    [RegressionInterpolationInputStepDataName.MODEL_VARIABLES]: [
      {
        stepId: newRegressionStep.id,
        stepDataName: RegressionStepDataName.MODEL_VARIABLES,
      },
    ],
  };
}

export default function InterpolationStepInputConfiguration({
  analysis,
  config,
  referenceStep,
  setConfig,
  setStepInputParents,
  stepInputParents,
}: StepInputParentsConfigurationProps): JSX.Element {
  const regressionStepId = getRegressionStepId(stepInputParents);

  const selectedStep =
    regressionStepId == null ? null : getLegacyNullableStepFromAnalysis(analysis, regressionStepId);
  function setSelectedStep(newStep: GetAnalysisPipelineStep): void {
    setStepInputParents(getUpdatedStepInputParentInformation(stepInputParents, newStep));

    // Remove selected partition value if defined
    if (config.hasOwnProperty('selectedPartitionValue')) {
      setConfig({...config, selectedPartitionValue: null});
    }
  }

  const partitionValueSelector = selectedStep != null && (
    <PartitionValueSelector config={config} selectedRegressionStep={selectedStep} setConfig={setConfig} />
  );

  return (
    <>
      <RegressionModelSelector
        analysis={analysis}
        inputStep={selectedStep}
        referenceStep={referenceStep}
        setInputStep={setSelectedStep}
      />
      {partitionValueSelector != null && partitionValueSelector}
    </>
  );
}
